import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Lightbulb } from '@factorialco/f0-react/icons/app'
import { Field } from 'factorial-form'
import {
  GenerateAutoScheduleDocument,
  ShiftManagementAutoScheduleStatusDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { Groups } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import EmployeesSelector from 'modules/shift_management/components/ShiftManagement/EmployeesSelector'
import useLocationIdsFilter from 'modules/shift_management/hooks/useLocationIdsFIlter'
import useLocationOptions from 'modules/shift_management/hooks/useLocationOptions'
import { useOnSelectEmployees } from 'modules/shift_management/hooks/useOnSelectEmployees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { AutoScheduleForm } from 'modules/shift_management/lib/forms/AutoScheduleForm'

import { Footer } from './Footer'
import { Rules } from './Rules'

interface Props {
  onClose: () => void
  setIsSubscribed: (isSubscribed: boolean) => void
}

const AutoScheduleModal = observer(({ onClose, setIsSubscribed }: Props) => {
  const apolloClient = useApolloClient()
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([])

  const [{ view }] = useSchedule()
  const form = useLazyRef(
    () =>
      new AutoScheduleForm({
        start_at: view.startAt,
        end_at: view.endAt,
      })
  ).current
  const { selectableEmployeeIds, loading: loadingEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt
  )
  const { onSelectEmployee } = useOnSelectEmployees({
    form,
    selectedEmployees,
    setSelectedEmployees,
  })

  const locationIds = useLocationIdsFilter()
  const locationField =
    locationIds.length === 1 ? new Field(locationIds.at(0), 'string') : new Field('', 'string')
  const locationId = Number(locationField.value)

  const { selectableEmployeeIds: preselectedEmployeesIds, loading: loadingPreselectedEmployees } =
    useSelectableEmployees(view.startAt, view.endAt, 'create', [locationId])

  const { locationOptions } = useLocationOptions({
    locationField,
  })

  const defaultLocation = locationOptions.find(
    (option) => option.value.toString() === locationField.value.toString()
  )

  useUnsafeEffect(() => {
    if (!preselectedEmployeesIds.length || loadingPreselectedEmployees) return

    setSelectedEmployees(() => preselectedEmployeesIds)
  }, [preselectedEmployeesIds.length, loadingPreselectedEmployees])

  useEffect(() => {
    const locationFormValue = form.get('location_id')
    locationFormValue.set(locationId)
  }, [locationId])

  useUnsafeEffect(() => {
    form.setValues({ employee_id: selectedEmployees })
  }, [selectedEmployees])

  const [createAutoSchedule, { loading }] = useMutation(GenerateAutoScheduleDocument, {
    extractorFn: (data) => data.shiftManagementMutations.generateScheduleShiftmanagementAutomation,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    track(trackingEvents.shiftManagement.autoSchedulerRun, {
      from: 'coverage_section',
      location_id: Number(form.get('location_id').value),
      agreement_hours: form.get('agreement_hours').value,
      max_work_hours_per_day: form.get('max_work_hours_per_day').value,
      rest_days_per_week: form.get('rest_days_per_week').value,
      rest_hours_between_shifts: form.get('rest_hours_between_shifts').value,
      start_date: form.get('start_at').value,
      end_date: form.get('end_at').value,
      employees_count: form.get('employee_id').value.length,
    })

    const res = await createAutoSchedule({
      variables: {
        agreementHours: form.get('agreement_hours').value,
        employeesIds: form.get('employee_id').value,
        endAt: form.get('end_at').value,
        locationId: Number(form.get('location_id').value),
        maxWorkHoursPerDay: form.get('max_work_hours_per_day').value,
        restDaysPerWeek: form.get('rest_days_per_week').value,
        restHoursBetweenShifts: form.get('rest_hours_between_shifts').value,
        startAt: form.get('start_at').value,
      },
    })
    apolloClient.refetchQueries({
      include: [ShiftManagementAutoScheduleStatusDocument],
    })
    const handled = handleResult(res, (_errors) => {})
    if (handled.success) {
      setIsSubscribed(true)
      closePortal()
    }
  }

  return (
    <Modal
      illustration="window-star"
      title={i18n.t('shift_management.coverage_section.automation.modal.title')}
      description={i18n.t('shift_management.coverage_section.automation.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Footer
          onClose={onClose}
          disabled={loading || selectedEmployees.length === 0 || !locationId}
        />
      }
      disableFocusTrap
    >
      <Box width="full" flexDirection="column" gap="s16">
        <DropdownSearchBar
          searchLabel={i18n.t('common.search')}
          sortLabel={i18n.t('common.name')}
          selectLabel={i18n.t('common.select')}
          labelNotFound={i18n.t('common.select')}
          label={i18n.t('shift_management.add_shift.location')}
          data={[]}
          name={defaultLocation?.label || i18n.t('common.select')}
          onSelect={() => {}}
          autofocus={false}
          disabled
        />

        <Rules form={form} />

        <Box background="viridian100" padding="s24" borderRadius={{ all: 'abs012' }}>
          <Box flexDirection="row" gap="s4">
            <F0Icon icon={Lightbulb} size="md" />
            <Text.Header3>
              {i18n.t('shift_management.coverage_section.automation.modal.info.title')}
            </Text.Header3>
          </Box>
          <Text.Normal>
            {i18n.t('shift_management.coverage_section.automation.modal.info.description')}
            <Link.Href href="" target="_blank">
              {i18n.t('common.learn_more')}
            </Link.Href>
          </Text.Normal>
        </Box>

        {loadingEmployees ? (
          <Loading />
        ) : (
          <EmployeesSelector
            label={i18n.t('common.employees')}
            selectableEmployeeIds={selectableEmployeeIds}
            onSelectEmployee={onSelectEmployee}
            selectedEmployeeIds={selectedEmployees}
            width="s400"
            defaultGroup={Groups.WORKPLACES}
          />
        )}
      </Box>
    </Modal>
  )
})

export { AutoScheduleModal }
